import { getModuleName, getModuleSubmodules } from '../../../../config/moduleCatalog';

const HALF_WIDTH = 'half-width';
const FULL_WIDTH = 'full-width';
export const FORM_REGISTRY_STORAGE_KEY = 'form-registry-schemas';
// Class B forms live in a logically separate "Supplemental Data Store" per
// the Instrument Classes & Dynamic Forms framework — kept in their own
// browser-storage key so they are never commingled with the frozen Core
// blueprints in FORM_REGISTRY_STORAGE_KEY.
export const SUPPLEMENTAL_FORM_STORAGE_KEY = 'form-registry-supplemental-schemas';
// Admin modifications to Standard (Class A) forms. Stored as whole modified
// forms keyed by id and laid over the code blueprints on read, so the code
// default is never lost and "Reset to Default" is just dropping the override.
export const STANDARD_OVERRIDES_STORAGE_KEY = 'form-registry-standard-overrides';

const DEFAULT_FORM_STATUS = 'published';
const DEFAULT_FORM_TYPE = 'standard';

export const REGISTRY_SUBMODULE_OPTIONS = {
  'all-core': [
    { value: 'registry-proposal', label: 'Registry Proposal' },
  ],
  'livestock-population': [
    { value: 'population', label: 'Livestock Population Records' },
  ],
  'markets-trade': [
    { value: 'registry', label: 'Market Registry' },
    { value: 'prices', label: 'Market Price Observation' },
    { value: 'border', label: 'Border Crossing Registry' },
    { value: 'vet', label: 'Veterinary Certificates Registry' },
  ],
  infrastructure: [
    { value: 'core', label: 'Facility Core Registry' },
    { value: 'status', label: 'Facility Status Update' },
  ],
  'workforce-institutional': [
    { value: 'actors', label: 'Livestock Actor Registration' },
    { value: 'deployment', label: 'Workforce Deployment' },
    { value: 'coop', label: 'Organisation / Cooperative Registration' },
  ],
  'planning-reporting': [
    { value: 'workplans', label: 'Workplan & Activities' },
    { value: 'reports', label: 'Activity Report' },
  ],
  'ppp-arrangements': [
    { value: 'arrangements', label: 'PPP Arrangement Registry' },
  ],
  'conflicts-dispute': [
    { value: 'records', label: 'Conflict Incident Report' },
    { value: 'committees', label: 'Conflict Committee Registration' },
    { value: 'routes', label: 'Transhumance Route Registration' },
  ],
};

export const FIELD_SOURCE_OPTIONS = [
  { value: '', label: 'Manual entry' },
  { value: 'coreModules', label: 'Core modules' },
  { value: 'livestockSubModules', label: 'Livestock submodules' },
  { value: 'marketSubModules', label: 'Market submodules' },
  { value: 'reference:species', label: 'Reference: Species' },
  { value: 'reference:breeds', label: 'Reference: Breeds' },
  { value: 'reference:productionSystems', label: 'Reference: Production systems' },
  { value: 'reference:productionPurposes', label: 'Reference: Production purposes' },
  { value: 'reference:countMethods', label: 'Reference: Count methods' },
  { value: 'reference:dataSources', label: 'Reference: Data sources' },
  { value: 'reference:ageClasses', label: 'Reference: Age classes' },
  { value: 'reference:institutions', label: 'Reference: Institutions' },
  { value: 'reference:markets', label: 'Reference: Markets' },
  { value: 'reference:borderCrossings', label: 'Reference: Border crossings' },
  { value: 'reference:vetCertificates', label: 'Reference: Vet certificates' },
  { value: 'reference:conflictCommittees', label: 'Reference: Conflict committees' },
  { value: 'reference:facilities', label: 'Reference: Facilities' },
  { value: 'reference:livestockActors', label: 'Reference: Livestock actors' },
  { value: 'reference:workplanActivities', label: 'Reference: Workplan activities' },
  { value: 'reference:facilityClimateAssessments', label: 'Reference: Approved facility climate assessments' },
];

function titleCase(value) {
  const text = String(value ?? '').trim();
  if (!text) return '';
  return text
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function createTimestamp() {
  return new Date().toISOString();
}

function scopeSectionFieldValue(section, fieldId) {
  return section?.fields?.find((field) => field.id === fieldId)?.value ?? '';
}

function isScopeSection(section) {
  const id = String(section?.id ?? '').trim().toLowerCase();
  const title = String(section?.title ?? '').trim().toLowerCase();
  return id.startsWith('select-module') || title === 'select module';
}

// Modules that are not one of the eight statistical core modules (so absent
// from coreModulesConfig.js) but still need a proper display name here.
const NON_CORE_MODULE_NAMES = {
  'ppp-arrangements': 'PPP Arrangement Registry (Cross-Cutting)',
  'all-core': 'Cross-Cutting (All Modules)',
};

// The eight canonical statistical modules are managed through the shared
// module catalog (rename + add-submodule, from the Module Scope panel) —
// everything else (cross-cutting registries like ppp-arrangements/all-core)
// keeps its own static name/submodule list above, unaffected.
const CATALOG_MODULE_IDS = new Set([
  'livestock-population',
  'markets-trade',
  'infrastructure',
  'workforce-institutional',
  'planning-reporting',
  'conflicts-dispute',
  'animal-identification',
  'animal-health',
]);

function resolveModuleName(moduleKey) {
  if (CATALOG_MODULE_IDS.has(moduleKey)) return getModuleName(moduleKey);
  return NON_CORE_MODULE_NAMES[moduleKey] ?? titleCase(moduleKey);
}

export function resolveSubmoduleName(moduleKey, submoduleKey) {
  const options = getRegistrySubmoduleOptions(moduleKey);
  return options.find((option) => option.value === submoduleKey)?.label ?? titleCase(submoduleKey);
}

function flattenFields(sections = []) {
  return sections.flatMap((section) => (Array.isArray(section.fields) ? section.fields : []));
}

function deriveScopeFromSections(sections = []) {
  const scopeSection = sections.find(isScopeSection) ?? null;
  if (!scopeSection) {
    return { moduleKey: '', submoduleKey: '' };
  }

  return {
    moduleKey: scopeSectionFieldValue(scopeSection, 'module'),
    submoduleKey: scopeSectionFieldValue(scopeSection, 'sub-module'),
  };
}

function stripScopeSection(sections = []) {
  return sections.filter((section) => !isScopeSection(section));
}

function slugify(value, fallback = 'item') {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || fallback;
}

function uniqueId(prefix, value) {
  const stamp = Date.now().toString(36);
  const random = Math.random().toString(36).slice(2, 7);
  return `${slugify(`${prefix}-${value}`, prefix)}-${stamp}-${random}`;
}

function createFieldId(field) {
  return uniqueId('field', `${field.label}-${field.type}`);
}

function cloneField(field) {
  return {
    ...field,
    id: field.id ?? createFieldId(field),
    options: Array.isArray(field.options) ? [...field.options] : field.options,
    validation: field.validation ? { ...field.validation } : undefined,
  };
}

function cloneSection(section) {
  return {
    ...section,
    id: section.id ?? slugify(section.title, 'section'),
    fields: (section.fields ?? []).map(cloneField),
  };
}

export function cloneFormBlueprint(form) {
  const sections = (form.sections ?? []).map(cloneSection);
  return {
    ...form,
    id: form.id ?? slugify(form.form_name ?? form.name, 'form'),
    formKey: form.formKey ?? form.id ?? slugify(form.form_name ?? form.name, 'form'),
    name: form.name ?? form.form_name ?? '',
    form_name: form.form_name ?? form.name ?? '',
    type: String(form.type ?? form.form_type ?? DEFAULT_FORM_TYPE).toLowerCase(),
    form_type: String(form.form_type ?? form.type ?? DEFAULT_FORM_TYPE).toLowerCase() === 'custom'
      ? 'Custom'
      : 'Standard',
    status: String(form.status ?? DEFAULT_FORM_STATUS).toLowerCase(),
    version: Number(form.version ?? 1) || 1,
    moduleKey: form.moduleKey ?? form.module_key ?? '',
    submoduleKey: form.submoduleKey ?? form.submodule_key ?? '',
    moduleName: form.moduleName ?? form.module_name ?? '',
    submoduleName: form.submoduleName ?? form.subtitle ?? '',
    description: form.description ?? '',
    sections,
    fields: Array.isArray(form.fields) ? form.fields.map(cloneField) : flattenFields(sections).map(cloneField),
    createdAt: form.createdAt ?? null,
    updatedAt: form.updatedAt ?? null,
  };
}

export function normalizeFormBlueprint(form) {
  const cloned = cloneFormBlueprint(form);
  const derivedScope = deriveScopeFromSections(cloned.sections);
  const moduleKey = cloned.moduleKey || derivedScope.moduleKey || '';
  const submoduleKey = cloned.submoduleKey || derivedScope.submoduleKey || '';
  const sections = stripScopeSection(cloned.sections);
  const moduleName = cloned.moduleName || resolveModuleName(moduleKey);
  const submoduleName = cloned.submoduleName || resolveSubmoduleName(moduleKey, submoduleKey);

  return {
    ...cloned,
    moduleKey,
    module_key: moduleKey,
    submoduleKey,
    submodule_key: submoduleKey,
    moduleName,
    module_name: moduleName,
    submoduleName,
    subtitle: submoduleName,
    sections,
    fields: flattenFields(sections).map(cloneField),
    createdAt: cloned.createdAt ?? null,
    updatedAt: cloned.updatedAt ?? null,
  };
}

function syncFormFields(form) {
  return {
    ...form,
    fields: flattenFields(form.sections).map(cloneField),
    updatedAt: createTimestamp(),
  };
}

export function updateFormMetadata(form, metadata = {}) {
  const next = {
    ...form,
    ...metadata,
  };

  if (Object.prototype.hasOwnProperty.call(metadata, 'moduleKey')) {
    next.module_key = metadata.moduleKey;
    next.moduleName = metadata.moduleName ?? resolveModuleName(metadata.moduleKey);
    next.module_name = next.moduleName;
  }

  if (Object.prototype.hasOwnProperty.call(metadata, 'submoduleKey')) {
    next.submodule_key = metadata.submoduleKey;
    next.submoduleName = metadata.submoduleName ?? resolveSubmoduleName(next.moduleKey ?? form.moduleKey, metadata.submoduleKey);
    next.subtitle = next.submoduleName;
  }

  if (Object.prototype.hasOwnProperty.call(metadata, 'name')) {
    next.form_name = metadata.name;
  }

  if (Object.prototype.hasOwnProperty.call(metadata, 'type')) {
    next.form_type = String(metadata.type).toLowerCase() === 'custom' ? 'Custom' : 'Standard';
  }

  if (Object.prototype.hasOwnProperty.call(metadata, 'status')) {
    next.status = String(metadata.status).toLowerCase();
  }

  if (Object.prototype.hasOwnProperty.call(metadata, 'version')) {
    next.version = Number(metadata.version) || 1;
  }

  // Changing the module or sub module scope invalidates whatever fields were
  // already configured under the old scope — restart General Information
  // with a clean slate rather than carrying over fields that may not apply.
  const moduleChanged = Object.prototype.hasOwnProperty.call(metadata, 'moduleKey') && metadata.moduleKey !== form.moduleKey;
  const submoduleChanged = Object.prototype.hasOwnProperty.call(metadata, 'submoduleKey') && metadata.submoduleKey !== form.submoduleKey;
  if (moduleChanged || submoduleChanged) {
    next.sections = buildDraftSections();
  }

  return syncFormFields({
    ...next,
    fields: Array.isArray(next.fields) ? next.fields : form.fields,
  });
}

export function updateFieldDefinition(form, sectionId, fieldId, updates = {}) {
  return syncFormFields({
    ...form,
    sections: form.sections.map((section) => (
      section.id === sectionId
        ? {
            ...section,
            fields: section.fields.map((field) => (
              field.id === fieldId
                ? {
                    ...field,
                    ...updates,
                    options: Array.isArray(updates.options) ? updates.options.map((option) => (typeof option === 'object' ? { ...option } : option)) : field.options,
                    validation: updates.validation ? { ...(field.validation ?? {}), ...updates.validation } : (updates.validation === null ? undefined : field.validation),
                  }
                : field
            )),
          }
        : section
    )),
  });
}

function dropdownField(id, label, options, extra = {}) {
  return {
    id,
    type: 'dropdown',
    label,
    required: Boolean(extra.required),
    layout: extra.layout ?? HALF_WIDTH,
    placeholder: extra.placeholder ?? 'Select',
    options,
    source: extra.source,
    hint: extra.hint,
    value: extra.value,
  };
}

function textField(id, label, extra = {}) {
  return {
    id,
    type: 'text',
    label,
    required: Boolean(extra.required),
    layout: extra.layout ?? HALF_WIDTH,
    placeholder: extra.placeholder ?? 'Enter text',
    hint: extra.hint,
  };
}

function numberField(id, label, extra = {}) {
  return {
    id,
    type: 'number',
    label,
    required: Boolean(extra.required),
    layout: extra.layout ?? HALF_WIDTH,
    placeholder: extra.placeholder ?? '0',
    hint: extra.hint,
  };
}

// Tick one or more values. `exclusiveOptions` are values that clear every
// other tick when chosen (e.g. Sex → "Combined" covers Male and Female).
function checkboxField(id, label, options, extra = {}) {
  return {
    id,
    type: 'checkbox',
    label,
    required: Boolean(extra.required),
    layout: extra.layout ?? FULL_WIDTH,
    options,
    exclusiveOptions: extra.exclusiveOptions ?? [],
    hint: extra.hint,
  };
}

function radioField(id, label, options, extra = {}) {
  return {
    id,
    type: 'radio',
    label,
    required: Boolean(extra.required),
    layout: extra.layout ?? FULL_WIDTH,
    options,
    hint: extra.hint,
  };
}

function geolocationField(id, label, extra = {}) {
  return {
    id,
    type: 'geolocation',
    label,
    required: Boolean(extra.required),
    layout: extra.layout ?? FULL_WIDTH,
    hint: extra.hint,
  };
}

function attachmentField(id, label, extra = {}) {
  return {
    id,
    type: 'attachment',
    label,
    required: Boolean(extra.required),
    layout: extra.layout ?? FULL_WIDTH,
    hint: extra.hint ?? 'Image or PDF upload — max 10MB',
  };
}

const MODULE_OPTIONS = [
  'All Core Modules',
  'Livestock Population',
  'Markets & Trade Intelligence',
  'Infrastructure & Services',
  'Workforce & Institutional Actors',
  'Planning & Reporting',
  'Conflicts & Dispute Records',
  'Animal Identification',
  'Animal Health',
];

const LIVESTOCK_SUBMODULE_OPTIONS = [
  'Population Records',
];

const MARKET_SUBMODULE_OPTIONS = [
  'Market Registry',
  'Market Price Observation',
  'Border Crossing Registry',
  'Veterinary Certificates Registry',
];

const PERIOD_TYPE_OPTIONS = ['Baseline', 'Monthly', 'Quarterly', 'Annual'];
const PERIOD_INSTANCE_OPTIONS = ['2026-Q1', '2026-Q2', '2026-Q3', '2026-Q4'];
const PRODUCTION_PURPOSE_OPTIONS = ['Meat', 'Milk', 'Eggs', 'Breeding'];
const AGE_GROUP_OPTIONS = ['Young', 'Adult', 'Mixed'];
const BREED_OPTIONS = ['Bunaji', 'Yankasa', 'Sokoto Red', 'Boer'];
const MARKET_TYPE_OPTIONS = ['Live Animal', 'Abattoir Market', 'Processing Market', 'Input/Feed', 'Mixed', 'Border Market', 'Terminal Market'];
const MARKET_STATUS_OPTIONS = ['Active', 'Inactive', 'Seasonal', 'Under Rehabilitation'];
const MARKET_SCHEDULE_OPTIONS = ['Daily', 'Weekly', 'Bi-Weekly', 'Monthly', 'Periodic', 'As Needed'];
const PRICE_UNIT_OPTIONS = ['Per Head', 'Per Kg Liveweight', 'Per Kg Carcass', 'Per Crate (30)', 'Per Bag (50kg)', 'Per Litre'];
const PRICE_TYPE_OPTIONS = ['Farm Gate', 'Wholesale', 'Retail', 'Auction'];
const SEASONAL_OPTIONS = ['Regular', 'Festival Peak Sallah', 'Festival Peak Christmas', 'Seasonal Low', 'Other'];
const GRADE_OPTIONS = ['Grade 1', 'Grade 2', 'Grade 3', 'Ungraded'];
const PURPOSE_OPTIONS = ['Trade', 'Movement', 'Export', 'Slaughter', 'Research', 'Import', 'Transit'];
const CERT_STATUS_OPTIONS = ['Valid', 'Expired', 'Revoked'];
const INSTITUTION_OPTIONS = ['NADIS', 'NAITS', 'State Veterinary Office'];

export const FIELD_SELECTOR_TEMPLATES = [
  {
    type: 'dropdown',
    label: 'Dropdown',
    description: 'Pick one value from a list of options.',
  },
  {
    type: 'text',
    label: 'Text',
    description: 'Short free-form text input.',
  },
  {
    type: 'number',
    label: 'Number',
    description: 'Numeric input for counts or amounts.',
  },
  {
    type: 'checkbox',
    label: 'Checkbox',
    description: 'Tick one or more values from a small set.',
  },
  {
    type: 'radio',
    label: 'Radio',
    description: 'Choose a single value from a small set.',
  },
  {
    type: 'geolocation',
    label: 'Geo Location',
    description: 'Select State, LGA and Ward.',
  },
  {
    type: 'gps',
    label: 'Geo Coordinate',
    description: 'Capture a GPS coordinate or waypoint.',
  },
  {
    type: 'attachment',
    label: 'Attached',
    description: 'Upload an image or document — max 10MB.',
  },
];

export function createFieldBlueprint(type) {
  switch (type) {
    case 'dropdown':
      return dropdownField(uniqueId('dropdown', 'field'), 'Dropdown', ['Option 1', 'Option 2'], { placeholder: 'Select' });
    case 'text':
      return textField(uniqueId('text', 'field'), 'Text', { placeholder: 'Enter text' });
    case 'number':
      return numberField(uniqueId('number', 'field'), 'Number', { placeholder: '0' });
    case 'checkbox':
      return checkboxField(uniqueId('checkbox', 'field'), 'Checkbox', ['Option 1', 'Option 2']);
    case 'radio':
      return radioField(uniqueId('radio', 'field'), 'Radio', ['Option 1', 'Option 2']);
    case 'geolocation':
      return geolocationField(uniqueId('geo', 'field'), 'Geo Location');
    case 'gps':
      return {
        id: uniqueId('gps', 'field'),
        type: 'gps',
        label: 'GPS Coordinates',
        required: false,
        layout: FULL_WIDTH,
        placeholder: 'Tap to capture a waypoint',
      };
    case 'attachment':
      return attachmentField(uniqueId('attachment', 'field'), 'Attached');
    default:
      return textField(uniqueId('text', 'field'), 'Text');
  }
}

function buildDraftSections() {
  return [
    {
      id: 'general-information',
      title: 'General Information',
      fields: [],
    },
  ];
}

// Every Supplemental (Class B) form must declare Owner, Sponsor, Purpose,
// Start/End date, Status, geographic scope, target role, retention policy
// and a PII flag (Dynamic Form Builder framework, section D). createdBy
// drives the "creator may not approve their own form" rule.
export function createDraftForm({ createdBy = '' } = {}) {
  return normalizeFormBlueprint({
    id: uniqueId('create-new-form', 'draft'),
    formKey: uniqueId('create-new-form', 'draft'),
    name: '',
    type: 'custom',
    status: DEFAULT_FORM_STATUS,
    version: 1,
    moduleKey: '',
    submoduleKey: '',
    moduleName: 'Form Designer',
    submoduleName: '',
    description: '',
    createdBy,
    sections: buildDraftSections(),
  });
}

export function appendFieldToSection(form, sectionId, field) {
  return syncFormFields({
    ...form,
    sections: form.sections.map((section) => (
      section.id === sectionId
        ? { ...section, fields: [...section.fields, field] }
        : section
    )),
  });
}

export function updateFieldValue(form, sectionId, fieldId, value) {
  return syncFormFields({
    ...form,
    sections: form.sections.map((section) => (
      section.id === sectionId
        ? { ...section, fields: section.fields.map((field) => (field.id === fieldId ? { ...field, value } : field)) }
        : section
    )),
  });
}

export function removeFieldFromSection(form, sectionId, fieldId) {
  return syncFormFields({
    ...form,
    sections: form.sections.map((section) => (
      section.id === sectionId
        ? { ...section, fields: section.fields.filter((field) => field.id !== fieldId) }
        : section
    )),
  });
}

export function insertSectionAfter(form, afterSectionId, title) {
  const index = form.sections.findIndex((section) => section.id === afterSectionId);
  const newSection = { id: uniqueId('section', title), title, fields: [] };
  const sections = [...form.sections];
  sections.splice(index === -1 ? sections.length : index + 1, 0, newSection);
  return syncFormFields({ ...form, sections });
}

export function renameSectionInForm(form, sectionId, title) {
  return syncFormFields({
    ...form,
    sections: form.sections.map((section) => (
      section.id === sectionId
        ? { ...section, title }
        : section
    )),
  });
}

export function upsertFieldInSection(form, sectionId, field) {
  return syncFormFields({
    ...form,
    sections: form.sections.map((section) => (
      section.id === sectionId
        ? {
            ...section,
            fields: section.fields.some((item) => item.id === field.id)
              ? section.fields.map((item) => (item.id === field.id ? field : item))
              : [...section.fields, field],
          }
        : section
    )),
  });
}

export function matchesRegistryQuery(form, query) {
  const needle = String(query ?? '').trim().toLowerCase();
  if (!needle) return true;
  return [
    form.name,
    form.form_name,
    form.type,
    form.form_type,
    form.status,
    form.moduleName,
    form.module_name,
    form.submoduleName,
    form.subtitle,
    String(form.version ?? ''),
    ...(form.sections ?? []).map((section) => section.title),
    ...(form.sections ?? []).flatMap((section) => (section.fields ?? []).map((field) => field.label)),
  ].some((value) => String(value ?? '').toLowerCase().includes(needle));
}

function createScopeSection(sectionId, moduleValue, submoduleValue, submoduleOptions) {
  return {
    id: sectionId,
    title: 'Select Module',
    locked: true,
    fields: [
      dropdownField('module', 'Module', MODULE_OPTIONS, {
        required: true,
        placeholder: 'Select Module',
        source: 'coreModules',
        value: moduleValue,
      }),
      dropdownField('sub-module', 'Sub Module', submoduleOptions, {
        required: true,
        placeholder: 'Select Sub Module',
        value: submoduleValue,
      }),
    ],
  };
}

export const FORM_REGISTRY_BLUEPRINTS = [
  cloneFormBlueprint({
    id: 'livestock-population-count',
    form_name: 'Livestock Population Count',
    form_type: 'Standard',
    module_name: 'Livestock Population',
    subtitle: 'Livestock Population',
    sections: [
      {
        id: 'select-module',
        title: 'Select Module',
        locked: true,
        fields: [
          dropdownField('module', 'Module', MODULE_OPTIONS, {
            required: true,
            placeholder: 'Select Module',
            source: 'coreModules',
            value: 'livestock-population',
          }),
          dropdownField('sub-module', 'Sub Module', LIVESTOCK_SUBMODULE_OPTIONS, {
            required: true,
            placeholder: 'Select Sub Module',
            source: 'livestockSubModules',
            value: 'population',
          }),
        ],
      },
      {
        id: 'geospatial',
        title: 'Geospatial',
        fields: [
          geolocationField('geospatial', 'Geospatial', {
            required: true,
          }),
        ],
      },
      {
        id: 'reference-period',
        title: 'Reference Period',
        fields: [
          dropdownField('period-type', 'Period Type', PERIOD_TYPE_OPTIONS, {
            required: true,
            placeholder: 'Select',
          }),
          dropdownField('period-instance', 'Period Instance', PERIOD_INSTANCE_OPTIONS, {
            required: true,
            placeholder: 'State type first',
          }),
        ],
      },
      {
        id: 'animal-data',
        title: 'Animal Data',
        fields: [
          dropdownField('livestock-species', 'Livestock Species', ['Cattle', 'Goat', 'Sheep', 'Poultry'], {
            required: true,
            layout: FULL_WIDTH,
            placeholder: 'Select',
            source: 'reference:species',
          }),
          dropdownField('production-system', 'Production System', ['Pastoral', 'Agropastoral', 'Intensive', 'Semi-Intensive', 'Backyard', 'Nomadic'], {
            required: true,
            placeholder: 'Select',
            source: 'reference:productionSystems',
          }),
          dropdownField('production-purpose', 'Production Purpose', PRODUCTION_PURPOSE_OPTIONS, {
            required: true,
            placeholder: 'State type first',
            source: 'reference:productionPurposes',
          }),
          numberField('population-count', 'Population Count', {
            required: true,
            layout: FULL_WIDTH,
            placeholder: '0',
            hint: 'Total animals, colonies for bees. Must be > 0.',
          }),
        ],
      },
      {
        id: 'classification',
        title: 'Classification',
        fields: [
          checkboxField('sex', 'Sex', ['Male', 'Female', 'Combined'], {
            required: true,
            layout: FULL_WIDTH,
            exclusiveOptions: ['Combined'],
            hint: 'Tick Male and/or Female (one record per sex), or Combined for one count covering both.',
          }),
          dropdownField('age-group', 'Age Group', AGE_GROUP_OPTIONS, {
            required: true,
            layout: FULL_WIDTH,
            placeholder: 'Select',
            source: 'reference:ageClasses',
          }),
          numberField('number-of-herds-flocks', 'Number of Herds / Flocks', {
            required: true,
            placeholder: '0',
          }),
          numberField('period-offtake', 'Period Offtake', {
            required: true,
            placeholder: '0',
            hint: 'Sold or slaughtered this period - enter 0 if none.',
          }),
        ],
      },
      {
        id: 'temporal-source',
        title: 'Temporal Source',
        fields: [
          dropdownField('count-method', 'Count Method', ['Physical Count', 'NAITS Registry', 'LGA Records', 'Estimate', 'Aerial Survey'], {
            required: true,
            placeholder: 'Select',
            source: 'reference:countMethods',
          }),
          dropdownField('data-source', 'Data Source', ['FMLD Field Count', 'LGA Report', 'State ADP', 'NBS Survey', 'Estimate'], {
            required: true,
            placeholder: 'Select',
            source: 'reference:dataSources',
          }),
        ],
      },
      {
        id: 'registry',
        title: 'Registry',
        fields: [
          dropdownField('breed-ecotype', 'Breed / Ecotype', BREED_OPTIONS, {
            required: true,
            layout: FULL_WIDTH,
            placeholder: 'Select breed or ecotype',
            source: 'reference:breeds',
          }),
        ],
      },
      {
        id: 'system',
        title: 'System',
        fields: [
          textField('confidence-level', 'Confidence Level', {
            hint: 'Auto-derived from Count Method (Physical Count -> High, Estimate -> Low, else Medium); collector may reduce, not raise. Not entered directly.',
          }),
        ],
      },
    ],
  }),
  cloneFormBlueprint({
    id: 'market-registration',
    form_name: 'Market Registration',
    form_type: 'Standard',
    module_name: 'Markets & Trade Intelligence',
    subtitle: 'Market Registry',
    sections: [
      {
        id: 'select-module-market',
        title: 'Select Module',
        locked: true,
        fields: [
          dropdownField('module', 'Module', MODULE_OPTIONS, {
            required: true,
            placeholder: 'Select Module',
            source: 'coreModules',
            value: 'markets-trade',
          }),
          dropdownField('sub-module', 'Sub Module', MARKET_SUBMODULE_OPTIONS, {
            required: true,
            placeholder: 'Select Sub Module',
            source: 'marketSubModules',
            value: 'registry',
          }),
        ],
      },
      {
        id: 'geospatial-market',
        title: 'Geospatial',
        fields: [
          geolocationField('geospatial', 'Geospatial', {
            required: true,
            hint: 'State -> LGA',
          }),
        ],
      },
      {
        id: 'market-data',
        title: 'Market Data',
        fields: [
          textField('market-name', 'Market Name', {
            required: true,
            layout: FULL_WIDTH,
            placeholder: 'e.g. Ikeja Central Market',
            hint: 'Official name — canonical+alias search before create',
          }),
          dropdownField('market-type', 'Market Type', MARKET_TYPE_OPTIONS, {
            required: true,
            placeholder: 'Select market type',
          }),
          dropdownField('market-status', 'Status', MARKET_STATUS_OPTIONS, {
            required: true,
            placeholder: 'Select status',
          }),
          dropdownField('market-schedule', 'Market Schedule', MARKET_SCHEDULE_OPTIONS, {
            placeholder: 'Select schedule',
          }),
          geolocationField('gps-coordinates', 'GPS Coordinates', {
            hint: 'WGS84 — required V1 for map pin',
          }),
        ],
      },
      {
        id: 'market-system',
        title: 'System',
        fields: [
          textField('data-status', 'Data Status', {
            hint: 'Auto — pipeline-controlled (draft/submitted/validated/approved/rejected). Not entered directly.',
          }),
        ],
      },
    ],
  }),
  cloneFormBlueprint({
    id: 'market-price-observation',
    form_name: 'Market Price Observation',
    form_type: 'Standard',
    module_name: 'Markets & Trade Intelligence',
    subtitle: 'Market Price',
    sections: [
      {
        id: 'select-module-price',
        title: 'Select Module',
        locked: true,
        fields: [
          dropdownField('module', 'Module', MODULE_OPTIONS, {
            required: true,
            placeholder: 'Select Module',
            source: 'coreModules',
            value: 'markets-trade',
          }),
          dropdownField('sub-module', 'Sub Module', MARKET_SUBMODULE_OPTIONS, {
            required: true,
            placeholder: 'Select Sub Module',
            source: 'marketSubModules',
            value: 'prices',
          }),
        ],
      },
      {
        id: 'price-location',
        title: 'Geospatial',
        fields: [
          geolocationField('geospatial', 'Geospatial', {
            required: true,
          }),
        ],
      },
      {
        id: 'market-price-data',
        title: 'Market Price Data',
        fields: [
          dropdownField('market', 'Market', ['Kano Main Market', 'Ikeja Market', 'Alausa Market'], {
            required: true,
            layout: FULL_WIDTH,
            placeholder: 'Select',
            source: 'reference:markets',
          }),
          textField('price-date', 'Price Date', {
            required: true,
            placeholder: 'YYYY-MM-DD',
            hint: 'Must be on or before today — R05 limited to the last 14 days',
          }),
          dropdownField('species', 'Species', ['Cattle', 'Goat', 'Sheep', 'Poultry'], {
            required: true,
            placeholder: 'Select',
            source: 'reference:species',
          }),
          dropdownField('commodity-form', 'Commodity Form', ['Live Animal', 'Carcass Whole', 'Quarters', 'Cut', 'Processed', 'By-Product'], {
            required: true,
            placeholder: 'Select',
          }),
          numberField('price', 'Price (NGN)', {
            required: true,
            placeholder: '0',
          }),
          dropdownField('price-unit', 'Price Unit', PRICE_UNIT_OPTIONS, {
            required: true,
            placeholder: 'Select',
          }),
          radioField('price-type', 'Price Type', PRICE_TYPE_OPTIONS, {
            required: true,
            layout: FULL_WIDTH,
          }),
          radioField('seasonal-flag', 'Seasonal Flag', SEASONAL_OPTIONS, {
            required: true,
            layout: FULL_WIDTH,
          }),
          numberField('volume-traded', 'Volume Traded', {
            required: true,
            placeholder: '0',
            hint: 'Enter 0 if not observed.',
          }),
          dropdownField('grade-quality', 'Grade / Quality', GRADE_OPTIONS, {
            placeholder: 'Optional',
          }),
        ],
      },
      {
        id: 'market-price-source',
        title: 'Temporal Source',
        fields: [
          dropdownField('data-source', 'Data Source', ['FMLD Field Officer', 'State ADP', 'LGA Report', 'NAQS', 'Other'], {
            required: true,
            placeholder: 'Select source',
            source: 'reference:dataSources',
          }),
        ],
      },
    ],
  }),
  cloneFormBlueprint({
    id: 'veterinary-certificate',
    form_name: 'Veterinary Certificate',
    form_type: 'Standard',
    module_name: 'Markets & Trade Intelligence',
    subtitle: 'Veterinary Certificates Registry',
    sections: [
      {
        id: 'select-module-vet',
        title: 'Select Module',
        locked: true,
        fields: [
          dropdownField('module', 'Module', MODULE_OPTIONS, {
            required: true,
            placeholder: 'Select Module',
            source: 'coreModules',
            value: 'markets-trade',
          }),
          dropdownField('sub-module', 'Sub Module', MARKET_SUBMODULE_OPTIONS, {
            required: true,
            placeholder: 'Select Sub Module',
            source: 'marketSubModules',
            value: 'vet',
          }),
        ],
      },
      {
        id: 'geospatial-vet',
        title: 'Geospatial',
        fields: [
          geolocationField('geospatial', 'Geospatial', {
            required: true,
          }),
        ],
      },
      {
        id: 'certification-registration',
        title: 'Certification Registration',
        fields: [
          textField('certificate-number', 'Certificate Number', {
            required: true,
            layout: FULL_WIDTH,
            placeholder: 'e.g. VET-202408-0001',
          }),
          radioField('status', 'Status', CERT_STATUS_OPTIONS, {
            required: true,
            layout: FULL_WIDTH,
          }),
          textField('issuing-officer', 'Issuing Officer', {
            required: true,
            placeholder: 'e.g. Dr. A. Bello',
          }),
          dropdownField('issuing-authority', 'Issuing Authority', INSTITUTION_OPTIONS, {
            required: true,
            placeholder: 'Select authority',
            source: 'reference:institutions',
          }),
          dropdownField('origin-market', 'Origin Market', ['Ikeja Market', 'Kano Main Market', 'Alausa Market'], {
            required: true,
            placeholder: 'Select market',
            source: 'reference:markets',
          }),
          numberField('head-count', 'Head Count / Quantity', {
            required: true,
            placeholder: '100',
          }),
          dropdownField('purpose', 'Purpose', PURPOSE_OPTIONS, {
            required: true,
            placeholder: 'Select purpose',
          }),
          textField('destination', 'Destination', {
            required: true,
            layout: FULL_WIDTH,
            placeholder: 'e.g. Kano central abattoir',
          }),
          dropdownField('species-covered', 'Species / Commodity Covered', ['Cattle', 'Goat', 'Sheep', 'Poultry'], {
            required: true,
            layout: FULL_WIDTH,
            placeholder: 'Select species',
            source: 'reference:species',
          }),
          textField('linked-border-crossing', 'Linked Border Crossing Reference', {
            layout: FULL_WIDTH,
            placeholder: 'Optional reference',
          }),
          textField('notes', 'Notes', {
            layout: FULL_WIDTH,
            placeholder: 'Optional notes',
          }),
        ],
      },
    ],
  }),
  cloneFormBlueprint({
    id: 'facility-registration-assessment',
    form_name: 'Facility Registration / Assessment',
    form_type: 'Standard',
    module_name: 'Infrastructure & Services',
    subtitle: 'Facility Core Registry',
    sections: [
      createScopeSection('select-module-infrastructure-core', 'infrastructure', 'core', REGISTRY_SUBMODULE_OPTIONS.infrastructure),
      {
        id: 'facility-geospatial',
        title: 'Geospatial',
        fields: [
          geolocationField('geospatial', 'Geospatial', {
            required: true,
          }),
          geolocationField('gps-coordinates', 'GPS Coordinates', {
            required: true,
            hint: 'From device GPS — MVP-mandatory map pin',
          }),
        ],
      },
      {
        id: 'facility-profile',
        title: 'Facility Profile',
        fields: [
          textField('facility-name', 'Facility Name', {
            required: true,
            layout: FULL_WIDTH,
            placeholder: 'e.g. Kano Abattoir',
          }),
          dropdownField('facility-type', 'Facility Type', [
            'Veterinary Clinic', 'Veterinary Hospital', 'Laboratory', 'Diagnostic Centre',
            'Live Animal Market', 'Abattoir', 'Slaughter Slab', 'Grazing Reserve',
            'Watering Point', 'Milk Collection Centre', 'Feed Mill',
            'Cold Storage (Commercial)', 'Cold Storage (Biological)', 'Breeding Farm',
            'AI Centre', 'Quarantine Station', 'Livestock Farm', 'Biogas System', 'Other',
          ], {
            required: true,
            placeholder: 'Select facility type',
            hint: 'Drives the conditional Capacity and Climate sections. Livestock Farm and Biogas System added in v2.2.',
          }),
          dropdownField('ownership', 'Ownership', ['Federal', 'State', 'LGA', 'Private', 'Community', 'NGO Partner', 'PPP', 'Religious'], {
            required: true,
            placeholder: 'Select ownership',
          }),
          dropdownField('licensed-status', 'Licensed Status', ['Licensed', 'Unlicensed', 'Pending', 'Exempt'], {
            required: true,
            placeholder: 'Select licensed status',
          }),
        ],
      },
      {
        id: 'facility-capacity',
        title: 'Capacity Details',
        fields: [
          numberField('daily-slaughter-capacity', 'Daily Slaughter Capacity (Head/day)', {
            placeholder: 'e.g. 200',
            hint: 'Conditionally mandatory — applies to Abattoir / Slaughter Slab facility types',
          }),
          numberField('annual-production-capacity-mt', 'Annual Production Capacity (Tonnes/year)', {
            placeholder: 'e.g. 5000',
            hint: 'Conditionally mandatory — applies to Feed Mill facility type',
          }),
          numberField('cold-storage-capacity-mt', 'Cold Storage Capacity (Tonnes)', {
            placeholder: 'e.g. 200',
            hint: 'Conditionally mandatory — applies to Cold Storage facility types',
          }),
          textField('cold-storage-type', 'Cold Storage Type', {
            placeholder: 'e.g. Refrigerated',
            hint: 'Conditionally mandatory — applies to Cold Storage facility types',
          }),
          numberField('daily-milk-capacity-litres', 'Daily Milk Capacity (Litres/day)', {
            placeholder: 'e.g. 500',
            hint: 'Conditionally mandatory — applies to Milk Collection Centre facility type',
          }),
          textField('water-source', 'Water Source', {
            placeholder: 'e.g. River, Borehole, Dam',
            hint: 'Conditionally mandatory — applies to Watering Point facility type',
          }),
        ],
      },
      {
        id: 'facility-assessment',
        title: 'Assessment',
        fields: [
          radioField('functional-status', 'Functional Status', ['Active', 'Inactive', 'Under Maintenance', 'Under Rehabilitation', 'Decommissioned'], {
            required: true,
            layout: FULL_WIDTH,
            hint: 'For a Biogas System, Active means operational.',
          }),
          textField('assessment-notes', 'Assessment Notes', {
            layout: FULL_WIDTH,
            placeholder: 'Optional notes',
            hint: 'Max 500 characters',
          }),
        ],
      },
      {
        id: 'facility-climate',
        title: 'Climate — Livestock Farm (v2.2 — shown when Facility Type = Livestock Farm)',
        fields: [
          checkboxField('csa-practices-adopted', 'Climate-Smart Practices Adopted', ['None', 'Improved Feeding', 'Improved Pasture / Forage', 'Manure Management', 'Biogas', 'Composting', 'Rotational Grazing', 'Silvopasture / Agroforestry', 'Improved Breeds', 'Herd Health & Productivity', 'Water Harvesting', 'Renewable Energy', 'Other'], {
            required: true,
            exclusiveOptions: ['None'],
            hint: 'F9 list (placeholder until D-015). None is explicit and cannot be combined.',
          }),
          dropdownField('manure-management-system', 'Manure Management System', ['Pasture / Range / Paddock', 'Daily Spread', 'Solid Storage', 'Dry Lot', 'Liquid Slurry', 'Anaerobic Digester', 'Composting', 'Burned For Fuel', 'Deep Bedding', 'Other'], {
            required: true,
            placeholder: 'Select manure system',
            hint: 'IPCC categories (F9).',
          }),
          dropdownField('feeding-regime', 'Feeding Regime', ['Grazing Only', 'Grazing + Supplement', 'Crop Residue Based', 'Stall Fed Forage', 'Concentrate Based', 'Mixed'], {
            placeholder: 'Select feeding regime',
          }),
          checkboxField('renewable-energy-on-site', 'Renewable Energy On Site', ['None', 'Solar', 'Biogas', 'Wind', 'Other'], {
            exclusiveOptions: ['None'],
          }),
          dropdownField('biogas-system-ref', 'Biogas System on this Farm', [], {
            source: 'reference:facilities',
            placeholder: 'Select from registry',
            hint: 'Link only — the digester is its own Biogas System facility.',
          }),
          dropdownField('production-system-circularity', 'Production System Circularity', ['Conventional', 'Partially Circular', 'Circular', 'Near Net-Zero'], {
            placeholder: 'Select circularity',
            hint: 'Declared — counts only once verified (D-018).',
          }),
          dropdownField('operator-actor-id', 'Farm Owner / Operator', [], {
            layout: FULL_WIDTH,
            source: 'reference:livestockActors',
            placeholder: 'Select from registry',
            hint: 'Link only — no climate data is stored on the person.',
          }),
          textField('practice-verification-status', 'Practice Verification Status', {
            hint: 'Auto — set by an approved Facility Status Update climate assessment (CSA verification). Never keyed.',
          }),
          textField('mrv-participation-status', 'MRV+C Participation Status', {
            hint: 'Auto — set only by the MRV+C integration feed (D-016). Never keyed.',
          }),
        ],
      },
      {
        id: 'facility-biogas',
        title: 'Biogas System (v2.2 — shown when Facility Type = Biogas System)',
        fields: [
          dropdownField('biogas-host-type', 'Host Type', ['On Farm', 'Processing Facility', 'Community', 'Institutional'], {
            required: true,
            placeholder: 'Select host type',
          }),
          dropdownField('biogas-host-facility', 'Facility Served', [], {
            required: true,
            source: 'reference:facilities',
            placeholder: 'Select from registry',
            hint: 'The livestock farm or processing facility this digester serves.',
          }),
          numberField('digester-volume-m3', 'Digester Volume (m³)', {
            required: true,
            placeholder: 'e.g. 12',
            hint: 'Must be > 0.',
          }),
          textField('commissioning-date', 'Commissioning Date', {
            required: true,
            placeholder: 'YYYY-MM-DD',
            hint: 'Must be ≤ today.',
          }),
          checkboxField('biogas-feedstock', 'Feedstock', ['Cattle Dung', 'Poultry Litter', 'Pig Slurry', 'Abattoir Waste', 'Mixed'], {
            required: true,
          }),
          checkboxField('biogas-energy-use', 'Energy Use', ['Cooking', 'Lighting', 'Electricity', 'Heating', 'Flaring', 'Other']),
        ],
      },
      {
        id: 'facility-waste-recovery',
        title: 'Waste Recovery (v2.2 — shown for Abattoir, Slaughter Slab, Processing and Milk Collection Centre)',
        fields: [
          checkboxField('waste-recovery-system', 'Waste Recovery System', ['None', 'Biogas', 'Composting', 'Rendering', 'Blood Meal', 'Bone Meal', 'Hide / Skin Recovery', 'Effluent Reuse', 'Other'], {
            required: true,
            exclusiveOptions: ['None'],
            hint: 'F9 list (placeholder until D-015). None is explicit and cannot be combined.',
          }),
          dropdownField('recovery-status', 'Recovery Status', ['Implemented', 'Partially Implemented', 'Planned', 'None'], {
            placeholder: 'Select recovery status',
            hint: 'Required when Waste Recovery System is not None.',
          }),
          dropdownField('manure-waste-handling', 'Lairage / Pen Manure Handling', ['Pasture / Range / Paddock', 'Daily Spread', 'Solid Storage', 'Dry Lot', 'Liquid Slurry', 'Anaerobic Digester', 'Composting', 'Burned For Fuel', 'Deep Bedding', 'Other'], {
            placeholder: 'Select manure handling',
          }),
          textField('waste-recovery-approval-status', 'Waste Recovery Approval Status', {
            hint: 'Auto — set by an approved climate assessment (waste-recovery verification) or inspection. Never keyed.',
          }),
        ],
      },
    ],
  }),
  cloneFormBlueprint({
    id: 'facility-status-update',
    form_name: 'Facility Status Update',
    form_type: 'Standard',
    module_name: 'Infrastructure & Services',
    subtitle: 'Facility Status Update',
    sections: [
      createScopeSection('select-module-infrastructure-status', 'infrastructure', 'status', REGISTRY_SUBMODULE_OPTIONS.infrastructure),
      {
        id: 'status-update-facility',
        title: 'Facility',
        fields: [
          dropdownField('facility', 'Facility', [], {
            required: true,
            layout: FULL_WIDTH,
            source: 'reference:facilities',
            placeholder: 'Select from registry',
            hint: 'Must already exist in the registry — this form cannot create a facility. Every assessment belongs to this one facility.',
          }),
          radioField('update-type', 'Update Type', ['Status Update', 'Climate Assessment'], {
            required: true,
            hint: 'v2.2 type selector. Hidden from R05, who always records a Status Update.',
          }),
        ],
      },
      {
        id: 'status-update-status',
        title: 'Status Update (shown when Update Type = Status Update)',
        fields: [
          dropdownField('functional-status', 'Functional Status', ['Active', 'Inactive', 'Under Maintenance', 'Under Rehabilitation', 'Decommissioned'], {
            required: true,
            placeholder: 'Select status',
          }),
          geolocationField('gps-coordinates', 'GPS Coordinates', { hint: 'Optional — confirm location' }),
          textField('notes', 'Notes', { layout: FULL_WIDTH, placeholder: 'Optional notes' }),
        ],
      },
      {
        id: 'status-update-climate-assessment',
        title: 'Climate Assessment (v2.2 — shown when Update Type = Climate Assessment; created by R04/R03, approved by R02)',
        fields: [
          dropdownField('assessment-type', 'Assessment Type', ['GHG Baseline', 'Emissions Screening', 'CSA Verification', 'Waste Recovery Verification', 'MRV Verification'], {
            required: true,
            placeholder: 'Select assessment type',
          }),
          dropdownField('assessment-scope', 'Assessment Scope', ['Facility Only', 'State Baseline'], {
            required: true,
            placeholder: 'Select scope',
            hint: 'State Baseline marks the record as part of the State GHG baseline (D-020).',
          }),
          textField('assessment-date', 'Assessment Date', {
            required: true,
            placeholder: 'YYYY-MM-DD',
            hint: 'Must be ≤ today.',
          }),
          dropdownField('assessing-institution', 'Assessing Institution', [], {
            required: true,
            source: 'reference:institutions',
            placeholder: 'Select institution',
          }),
          dropdownField('linked-activity', 'Linked Activity', [], {
            layout: FULL_WIDTH,
            source: 'reference:workplanActivities',
            placeholder: 'Select activity (optional)',
            hint: 'Optional — the planned intervention being assessed.',
          }),
          dropdownField('verification-result', 'Verification Result', ['Verified', 'Partially Verified', 'Not Verified'], {
            placeholder: 'Select result',
            hint: 'Required for CSA / Waste Recovery / MRV verification. On approval it sets the facility verification status.',
          }),
          dropdownField('emissions-classification', 'Emissions Classification', ['Net Reduction', 'Emissions Neutral', 'Net Increase', 'Not Determinable'], {
            placeholder: 'Select classification',
            hint: 'Required for Emissions Screening — assigned by the assessor (D-017). NLIMS never computes it.',
          }),
          dropdownField('baseline-reference', 'Baseline Reference', [], {
            source: 'reference:facilityClimateAssessments',
            placeholder: 'Select approved baseline',
            hint: 'Required for Emissions Screening — an approved GHG Baseline assessment.',
          }),
          attachmentField('assessment-report', 'Assessment Report', {
            hint: 'PDF, max 10MB — required for GHG Baseline and Emissions Screening.',
          }),
          textField('assessment-outcome', 'Assessment Outcome', {
            hint: 'Auto — set by the approval workflow (pending approval / approved / returned). Read-only.',
          }),
        ],
      },
    ],
  }),
  cloneFormBlueprint({
    id: 'border-crossing-record',
    form_name: 'Border Crossing Record',
    form_type: 'Standard',
    module_name: 'Markets & Trade Intelligence',
    subtitle: 'Border Crossing Registry',
    sections: [
      createScopeSection('select-module-border', 'markets-trade', 'border', REGISTRY_SUBMODULE_OPTIONS['markets-trade']),
      {
        id: 'border-geospatial',
        title: 'Geospatial',
        fields: [geolocationField('geospatial', 'Geospatial', { required: true })],
      },
      {
        id: 'border-crossing-data',
        title: 'Border Crossing Data',
        fields: [
          dropdownField('border-point', 'Border Point', [], { required: true, layout: FULL_WIDTH, source: 'reference:borderCrossings', placeholder: 'Select border point' }),
          radioField('crossing-direction', 'Crossing Direction', ['Import', 'Export', 'Transit'], { required: true, layout: FULL_WIDTH }),
          dropdownField('species', 'Species / Livestock', ['Cattle', 'Goat', 'Sheep', 'Poultry'], { required: true, source: 'reference:species' }),
          numberField('head-count', 'Head Count', { required: true, placeholder: '0' }),
          textField('origin-country', 'Origin Country', { required: true, placeholder: 'Country or region of origin' }),
          textField('destination-country', 'Destination Country', { required: true, placeholder: 'Country or region of destination' }),
          textField('movement-date', 'Movement Date', { required: true, placeholder: 'YYYY-MM-DD' }),
          dropdownField('inspection-status', 'Inspection Status', ['Cleared', 'Held', 'Rejected'], { required: true, placeholder: 'Select status' }),
          textField('vet-certificate-ref', 'Veterinary Certificate Reference', { hint: 'Conditional — required for OPT-22' }),
          textField('movement-permit-number', 'Movement Permit Number', { placeholder: 'Optional permit number' }),
        ],
      },
    ],
  }),
  cloneFormBlueprint({
    id: 'registry-proposal',
    form_name: 'Registry Proposal',
    form_type: 'Standard',
    module_name: 'All Core Modules',
    subtitle: 'Cross-cutting Registry Proposal',
    sections: [
      createScopeSection('select-module-registry-proposal', 'all-core', 'registry-proposal', REGISTRY_SUBMODULE_OPTIONS['all-core']),
      {
        id: 'proposal-details',
        title: 'Registry Proposal',
        fields: [
          dropdownField('entity-type', 'Entity Type', ['Market', 'Facility', 'Actor', 'Cooperative', 'Conflict Committee', 'Transhumance Route'], { required: true, placeholder: 'Select entity type' }),
          textField('proposed-name', 'Proposed Name', { required: true, layout: FULL_WIDTH, placeholder: 'Enter the proposed name' }),
          geolocationField('geospatial', 'Geospatial', { required: true, hint: 'State locked by role' }),
          textField('classification', 'Classification', { required: true, layout: FULL_WIDTH, placeholder: 'e.g. live_animal market / feedmill' }),
          textField('notes-evidence', 'Notes / Evidence', { layout: FULL_WIDTH, placeholder: 'Optional — min detail for R04 validation' }),
        ],
      },
    ],
  }),
  cloneFormBlueprint({
    id: 'livestock-actor-registration',
    form_name: 'Livestock Actor Registration',
    form_type: 'Standard',
    module_name: 'Workforce & Institutional Actors',
    subtitle: 'Livestock Actor Registration',
    sections: [
      createScopeSection('select-module-workforce-actors', 'workforce-institutional', 'actors', REGISTRY_SUBMODULE_OPTIONS['workforce-institutional']),
      {
        id: 'actor-profile',
        title: 'Actor Profile',
        fields: [
          textField('full-name', 'Full Name', {
            required: true,
            layout: FULL_WIDTH,
            placeholder: 'e.g. Amina Musa',
          }),
          dropdownField('actor-type', 'Actor Type', ['Veterinarian', 'Animal Scientist', 'Extension Worker', 'Livestock Inspector', 'Abattoir Officer', 'Vet Paraprofessional', 'CAHW', 'Cooperative Officer', 'Trader', 'Farmer Producer', 'Processor', 'Transporter', 'Input Supplier', 'Researcher', 'Other'], {
            required: true,
            layout: FULL_WIDTH,
            placeholder: 'Select actor type',
          }),
          dropdownField('gender', 'Gender', ['Male', 'Female', 'Prefer Not To Say'], {
            required: true,
            placeholder: 'Select gender',
          }),
          dropdownField('age-group', 'Age Group', ['18 to 25', '26 to 35', '36 to 45', '46 to 55', '56+'], {
            required: true,
            placeholder: 'Select age group',
          }),
          textField('phone', 'Phone Number', {
            required: true,
            placeholder: 'e.g. 08030000000',
            hint: 'Nigerian format: 070/080/090... — numbers only, 11 digits',
          }),
          textField('email', 'Email Address', {
            placeholder: 'Optional email address',
          }),
          dropdownField('species-specialisation', 'Species Specialisation', ['Cattle', 'Goat', 'Sheep', 'Poultry', 'Pig', 'Camel'], {
            layout: FULL_WIDTH,
            placeholder: 'Select one or more species',
            source: 'reference:species',
            hint: 'Optional, multi-select — select all species this actor works with',
          }),
          textField('registration-number', 'Registration Number', {
            placeholder: 'ACT-[YEAR]-[SEQ]',
            hint: 'Assigned automatically on save',
          }),
        ],
      },
      {
        id: 'actor-location',
        title: 'Location',
        fields: [
          geolocationField('geospatial', 'Geospatial', {
            required: true,
            hint: 'LGA is mandatory — used as a ratio join key',
          }),
        ],
      },
      {
        id: 'actor-system',
        title: 'System',
        fields: [
          dropdownField('status', 'Status', ['Active', 'Inactive', 'Suspended'], {
            required: true,
            placeholder: 'Select status',
          }),
          dropdownField('verification-status', 'Verification Status', ['Pending', 'Verified', 'Rejected'], {
            hint: 'Auto — defaults to Pending; verified by R04+',
          }),
        ],
      },
      {
        id: 'actor-farmer-section',
        title: 'Farmer Registration (conditional — shown when Actor Type = Farmer Producer)',
        fields: [
          dropdownField('farmer-production-system', 'Production System', ['Pastoral', 'Agropastoral', 'Intensive', 'Semi Intensive', 'Backyard', 'Mixed'], {
            required: true,
            placeholder: 'Select production system',
          }),
          dropdownField('farmer-primary-species', 'Primary Species', [], {
            required: true,
            source: 'reference:species',
            placeholder: 'Select species',
          }),
          dropdownField('farmer-herd-size-class', 'Herd Size Class', ['Micro', 'Small', 'Medium', 'Large'], {
            required: true,
            placeholder: 'Select herd size class',
            hint: 'Per-species thresholds pending Livestock-Specialist sign-off (D-009)',
          }),
          numberField('farmer-farm-size', 'Farm Size (hectares)', { placeholder: '0', hint: '≥0.1 ha' }),
          textField('farmer-herd-registration-number', 'Herd Registration Number', { placeholder: 'Enter herd registration number' }),
          dropdownField('farmer-population-record', 'Population Record', [], { source: 'reference:populationRecords', placeholder: 'Select population record (optional)' }),
          dropdownField('farmer-primary-market', 'Primary Market', [], { source: 'reference:markets', placeholder: 'Select primary market (optional)' }),
          dropdownField('farmer-extension-officer-deployment', 'Extension Officer Deployment', [], { source: 'reference:deployments', placeholder: 'Select deployment (optional)', hint: 'Active extension officers in LGA' }),
        ],
      },
      {
        id: 'actor-qualification-section',
        title: 'Qualification (repeatable)',
        fields: [
          dropdownField('qualification-type', 'Qualification Type', ['Degree Vet Medicine', 'Degree Animal Science', 'Diploma', 'Certificate', 'Professional License', 'Short Course', 'Extension Training', 'CPD', 'Other'], { placeholder: 'Select type' }),
          textField('qualification-name', 'Qualification Name', { placeholder: 'Enter qualification name' }),
          dropdownField('issuing-institution', 'Issuing Institution', [], { source: 'reference:institutions', placeholder: 'Select institution' }),
          textField('issuing-authority', 'Issuing Authority', { placeholder: 'Enter issuing authority' }),
          textField('issue-date', 'Issue Date', { placeholder: 'YYYY-MM-DD' }),
          textField('expiry-date', 'Expiry Date', { placeholder: 'YYYY-MM-DD', hint: 'Required only when Qualification Type is Professional License' }),
          textField('credential-number', 'Credential Number', { placeholder: 'Enter credential number' }),
          textField('evidence-attachment', 'Evidence Attachment', { layout: FULL_WIDTH, hint: 'Image or PDF upload — max 10MB' }),
          dropdownField('qualification-verification-status', 'Verification Status', ['Pending', 'Verified', 'Rejected'], { placeholder: 'Select status' }),
        ],
      },
    ],
  }),
  cloneFormBlueprint({
    id: 'workforce-deployment',
    form_name: 'Workforce Deployment',
    form_type: 'Standard',
    module_name: 'Workforce & Institutional Actors',
    subtitle: 'Workforce Deployment',
    sections: [
      createScopeSection('select-module-workforce-deployment', 'workforce-institutional', 'deployment', REGISTRY_SUBMODULE_OPTIONS['workforce-institutional']),
      {
        id: 'deployment-details',
        title: 'Deployment Details',
        fields: [
          dropdownField('actor-id', 'Actor Reference', [], {
            required: true,
            layout: FULL_WIDTH,
            source: 'reference:livestockActors',
            placeholder: 'Select actor',
          }),
          dropdownField('role-category', 'Role Category', ['Veterinarian', 'Animal Scientist', 'Extension Worker', 'Livestock Inspector', 'Abattoir Officer', 'Vet Paraprofessional', 'CAHW', 'Cooperative Officer', 'Trader', 'Farmer Producer', 'Processor', 'Transporter', 'Input Supplier', 'Researcher', 'Other'], {
            required: true,
            placeholder: 'Select role category',
            hint: 'Matches the Actor Type vocabulary',
          }),
          dropdownField('institution-facility', 'Institution / Facility', [], {
            required: true,
            layout: FULL_WIDTH,
            source: 'reference:institutions',
            placeholder: 'Select posting institution',
          }),
          dropdownField('deployment-type', 'Deployment Type', ['Permanent', 'Temporary', 'Field Mission', 'Training Attachment', 'Emergency'], {
            required: true,
            placeholder: 'Select deployment type',
          }),
          dropdownField('status', 'Status', ['Active', 'Inactive', 'On Leave', 'Completed'], {
            required: true,
            placeholder: 'Select status',
          }),
          dropdownField('mobility-status', 'Mobility Status', ['Has Vehicle', 'Shared Vehicle', 'No Vehicle'], {
            placeholder: 'Optional',
          }),
          textField('state', 'State', { required: true, placeholder: 'Select state' }),
          textField('lga', 'LGA', { required: true, placeholder: 'Select LGA', hint: 'Mandatory — used as a ratio join key' }),
          textField('start-date', 'Start Date', {
            required: true,
            placeholder: 'YYYY-MM-DD',
          }),
          textField('end-date', 'End Date', {
            placeholder: 'YYYY-MM-DD',
            hint: 'Must be on or after Start Date',
          }),
        ],
      },
    ],
  }),
  cloneFormBlueprint({
    id: 'organisation-cooperative-registration',
    form_name: 'Organisation / Cooperative Registration',
    form_type: 'Standard',
    module_name: 'Workforce & Institutional Actors',
    subtitle: 'Organisation / Cooperative Registration',
    sections: [
      createScopeSection('select-module-workforce-coop', 'workforce-institutional', 'coop', REGISTRY_SUBMODULE_OPTIONS['workforce-institutional']),
      {
        id: 'cooperative-profile',
        title: 'Cooperative Profile',
        fields: [
          textField('cooperative-name', 'Cooperative Name', {
            required: true,
            layout: FULL_WIDTH,
            placeholder: 'e.g. Kano Grazers Cooperative',
          }),
          dropdownField('organisation-type', 'Organisation Type', ['Farmers Cooperative', 'Commodity Association', 'Processors Association', 'Traders Association', 'Womens Group', 'Youth Group', 'NGO', 'Private Company', 'Government Agency', 'Other'], {
            required: true,
            placeholder: 'Select organisation type',
          }),
          textField('registration-number', 'Registration Number', {
            required: true,
            placeholder: 'e.g. COOP-2026-001',
            hint: 'Min 5 characters',
          }),
          numberField('total-members', 'Total Members', {
            required: true,
            placeholder: '0',
            hint: 'Must be >= 1',
          }),
          numberField('active-members', 'Active Members', {
            required: true,
            placeholder: '0',
            hint: 'Must be <= Total Members',
          }),
          dropdownField('primary-commodity', 'Primary Commodity', ['Cattle', 'Goat', 'Sheep', 'Poultry', 'Pig', 'Camel'], {
            required: true,
            placeholder: 'Select commodity',
            source: 'reference:species',
          }),
          textField('state', 'State', {
            required: true,
            placeholder: 'State of registration',
          }),
          textField('lga', 'LGA', {
            required: true,
            placeholder: 'Local government area',
            hint: 'Mandatory — used as a ratio join key',
          }),
          dropdownField('bank-account-status', 'Bank Account Status', ['Yes', 'No', 'Pending'], {
            placeholder: 'Optional',
          }),
          textField('date-of-registration', 'Date of Registration', {
            placeholder: 'YYYY-MM-DD',
          }),
        ],
      },
    ],
  }),
  cloneFormBlueprint({
    id: 'workplan-activities',
    form_name: 'Workplan & Activities',
    form_type: 'Standard',
    module_name: 'Planning & Reporting',
    subtitle: 'Workplan Registration',
    sections: [
      createScopeSection('select-module-planning-workplan', 'planning-reporting', 'workplans', REGISTRY_SUBMODULE_OPTIONS['planning-reporting']),
      {
        id: 'workplan-header',
        title: 'Workplan Header',
        fields: [
          dropdownField('department', 'Department', ['Livestock Production', 'Animal Health', 'Markets & Trade', 'Infrastructure', 'Extension', 'Planning (DPRS)'], {
            required: true,
            placeholder: 'Select department',
          }),
          textField('plan-title', 'Plan Title', {
            required: true,
            layout: FULL_WIDTH,
            placeholder: '[Institution] - [Type] - [Period] - [Focus]',
            hint: 'Naming convention enforced. Minimum 10 characters.',
          }),
          radioField('plan-type', 'Plan Type', ['Annual', 'Medium Term', 'Emergency'], {
            required: true,
          }),
          radioField('plan-category', 'Plan Category', ['Workplan', 'Reform Plan'], {
            required: true,
            hint: 'v2.0 type selector — Reform Plan drives the conditional Reform section below.',
          }),
          textField('start-date', 'Start Date', {
            required: true,
            placeholder: 'YYYY-MM-DD',
            hint: 'Must not be in the past for a new plan.',
          }),
          textField('end-date', 'End Date', {
            required: true,
            placeholder: 'YYYY-MM-DD',
            hint: 'Must be after Start Date',
          }),
          numberField('budget-ngn', 'Budget (NGN)', {
            required: true,
            placeholder: '0',
            hint: 'Must be > 0',
          }),
          dropdownField('plan-owner', 'Plan Owner', [], {
            required: true,
            source: 'reference:livestockActors',
            placeholder: 'Select from registry',
            hint: 'Named officer — constituency / department / others.',
          }),
          radioField('geographic-scope', 'Geographic Scope', ['National', 'State Specific', 'LGA Targeted'], {
            required: true,
          }),
          dropdownField('status', 'Status', ['Draft', 'Approved', 'Active', 'Completed', 'Suspended', 'Cancelled'], {
            required: true,
            placeholder: 'Select status',
          }),
          textField('completion-percent', 'Completion Percent', {
            hint: 'Auto — completed activities ÷ total activities × 100. Derived read-only roll-up from linked Activity Reports.',
          }),
        ],
      },
      {
        id: 'workplan-activity-section',
        title: 'Linked Activity',
        fields: [
          textField('activity-title', 'Activity Title', {
            required: true,
            placeholder: 'Enter activity title',
            hint: 'Minimum 5 characters. At least one activity row is required.',
          }),
          dropdownField('activity-category', 'Category', ['Training', 'Field Visit', 'Survey', 'Procurement', 'Meeting', 'Construction', 'Campaign', 'Technical Assistance', 'Other'], {
            required: true,
            placeholder: 'Select category',
          }),
          numberField('activity-budget-ngn', 'Budget (NGN)', {
            required: true,
            placeholder: '0',
            hint: "Must be ≤ the parent workplan's remaining budget.",
          }),
          numberField('activity-target-beneficiaries', 'Target Beneficiaries', {
            required: true,
            placeholder: '0',
          }),
          dropdownField('activity-beneficiary-class', 'Beneficiary Class', ['Farmer', 'Herder', 'Processor', 'Trader', 'Cooperative', 'Women', 'Youth', 'Other'], {
            placeholder: 'Select beneficiary class (optional)',
          }),
          radioField('activity-climate-screening', 'Climate Screening', ['Not Applicable', 'Required'], {
            required: true,
            hint: 'v2.2 — defaults to Not Applicable. Required = the intervention is subject to emissions screening.',
          }),
          dropdownField('activity-target-facility', 'Target Facility', [], {
            layout: FULL_WIDTH,
            source: 'reference:facilities',
            placeholder: 'Select from registry',
            hint: 'v2.2 — shown and mandatory when Climate Screening = Required. The facility the intervention is assessed on.',
          }),
        ],
      },
      {
        id: 'workplan-reform-section',
        title: 'Reform (conditional — shown when Plan Category = Reform Plan)',
        fields: [
          dropdownField('reform-category', 'Reform Category', ['Policy Reform', 'Process Improvement', 'Digital Transformation', 'Service Delivery Improvement', 'Organisational Reform'], {
            placeholder: 'Select reform category',
          }),
          dropdownField('supporting-departments', 'Supporting Departments', ['Livestock Production', 'Animal Health', 'Markets & Trade', 'Infrastructure', 'Extension', 'Planning (DPRS)'], {
            layout: FULL_WIDTH,
            placeholder: 'Select one or more departments',
            hint: 'One or more — department vocabulary.',
          }),
        ],
      },
    ],
  }),
  cloneFormBlueprint({
    id: 'activity-report',
    form_name: 'Activity Report',
    form_type: 'Standard',
    module_name: 'Planning & Reporting',
    subtitle: 'Activity Reports',
    sections: [
      createScopeSection('select-module-planning-report', 'planning-reporting', 'reports', REGISTRY_SUBMODULE_OPTIONS['planning-reporting']),
      {
        id: 'activity-report-details',
        title: 'Activity Report Details',
        fields: [
          dropdownField('parent-activity-id', 'Parent Activity', [], {
            layout: FULL_WIDTH,
            source: 'reference:workplanActivities',
            placeholder: 'Select from registry',
            hint: 'Approved activities only. Required unless Activity Type is PPP Milestone (PPP ID links instead).',
          }),
          dropdownField('activity-type', 'Activity Type', ['Standard', 'Reform', 'Engagement', 'Coordination', 'Special Intervention', 'PPP Milestone'], {
            required: true,
            placeholder: 'Select activity type',
          }),
          dropdownField('ppp-id', 'PPP ID', [], {
            source: 'reference:pppArrangements',
            placeholder: 'Select from registry',
            hint: 'PPP Arrangement Registry. Mandatory when Activity Type is PPP Milestone.',
          }),
          dropdownField('reporting-period', 'Reporting Period', ['2026-Q1', '2026-Q2', '2026-Q3', '2026-Q4'], {
            required: true,
            placeholder: 'Select period',
          }),
          textField('actual-start-date', 'Actual Start Date', {
            required: true,
            placeholder: 'YYYY-MM-DD',
            hint: 'Must be ≤ today.',
          }),
          dropdownField('execution-status', 'Execution Status', ['Completed', 'Partially Completed', 'Not Started', 'Delayed', 'Cancelled'], {
            required: true,
            placeholder: 'Select status',
            hint: 'Never True/False.',
          }),
          numberField('actual-expenditure-ngn', 'Actual Expenditure (NGN)', {
            required: true,
            placeholder: '0',
            hint: 'Must be ≥ 0.',
          }),
          textField('outputs-achieved', 'Outputs Achieved', {
            layout: FULL_WIDTH,
            placeholder: 'e.g. 450 farmers trained, 12 LGAs',
            hint: 'Structured counts, not narrative.',
          }),
          textField('outputs-target', 'Outputs Target', {
            hint: "Auto — populated from the linked parent activity's target beneficiaries.",
          }),
          textField('variance-explanation', 'Variance Explanation', {
            layout: FULL_WIDTH,
            placeholder: 'Explain any variance',
            hint: 'Required (minimum 20 characters) whenever Execution Status is not Completed.',
          }),
          attachmentField('attachments', 'Attachments', {
            layout: FULL_WIDTH,
            hint: 'Image, video, link, or PDF — max 10MB.',
          }),
          dropdownField('facility-assessment-ref', 'Facility Climate Assessment', [], {
            layout: FULL_WIDTH,
            source: 'reference:facilityClimateAssessments',
            placeholder: 'Select approved assessment',
            hint: 'v2.2 — shown and mandatory when the parent activity has Climate Screening = Required and Execution Status is Completed. Links to the approved emissions-screening assessment on the target facility.',
          }),
          textField('submission-date', 'Submission Date', {
            hint: 'Auto — system-set on save.',
          }),
        ],
      },
    ],
  }),
  cloneFormBlueprint({
    id: 'conflict-incident-report',
    form_name: 'Conflict Incident Report',
    form_type: 'Standard',
    module_name: 'Conflicts & Dispute Records',
    subtitle: 'Conflict Incident Report',
    sections: [
      createScopeSection('select-module-conflict-records', 'conflicts-dispute', 'records', REGISTRY_SUBMODULE_OPTIONS['conflicts-dispute']),
      {
        id: 'conflict-geospatial',
        title: 'Geospatial',
        fields: [
          geolocationField('geospatial', 'Geospatial', {
            required: true,
          }),
        ],
      },
      {
        id: 'conflict-details',
        title: 'Conflict Details',
        fields: [
          dropdownField('conflict-type', 'Conflict Type', ['Grazing Dispute', 'Farm Destruction', 'Water Point Clash', 'Route Encroachment', 'Resource Access', 'Climate Related', 'Traditional Beliefs', 'Other'], {
            required: true,
            layout: FULL_WIDTH,
            placeholder: 'Select conflict type',
          }),
          dropdownField('severity-level', 'Severity Level', ['Low', 'Medium', 'High', 'Critical'], {
            required: true,
            hint: 'R05 sets initial; R04 revises',
          }),
          dropdownField('first-occurrence', 'First Occurrence', ['Yes, first occurrence', 'No, repeat incident'], {
            required: true,
          }),
          dropdownField('cause', 'Cause', ['Crop Damage', 'Water Access', 'Grazing Encroachment', 'Route Blockage', 'Seasonal Migration', 'Climate', 'Land Rights', 'Other'], {
            required: true,
            layout: FULL_WIDTH,
            placeholder: 'Select cause of conflict',
          }),
          dropdownField('parties-involved-type', 'Parties Involved', ['Farmer', 'Herder', 'Community', 'Security Agency', 'Traditional Institution', 'NGO Partner', 'Other'], {
            required: true,
            layout: FULL_WIDTH,
            hint: 'Multi-select — select all that apply',
          }),
          dropdownField('parties-production-type', 'Parties Production Type', ['Pastoralist', 'Agropastoral', 'Sedentary Farmer', 'Mixed'], {
            layout: FULL_WIDTH,
            hint: 'Multi-select — select all that apply',
          }),
          dropdownField('sex-of-primary-parties', 'Sex of Primary Parties', ['Male', 'Female', 'Mixed', 'Unknown'], {}),
          dropdownField('age-group-of-parties', 'Age Group of Parties', ['Youth Under 35', 'Adult', 'Mixed'], {}),
          numberField('livestock-lost', 'Livestock Lost', {
            placeholder: '0',
            hint: 'Killed/stolen/missing — enter 0, not blank',
          }),
          numberField('human-casualties', 'Human Casualties', {
            required: true,
            placeholder: '0',
            hint: 'Enter 0 if none — must not be left blank',
          }),
          numberField('economic-loss', 'Economic Loss (NGN)', {
            placeholder: '0',
            hint: 'Mandatory at validation (R04) — enter 0 if none',
          }),
          dropdownField('asset-damage', 'Asset Damage', ['Yes', 'No'], {
            required: true,
          }),
          dropdownField('reporting-authority', 'Reporting Authority', ['LGA Desk Officer', 'Security Agency', 'Traditional Leader', 'Agro Ranger', 'NGO Partner', 'Community Peace Committee'], {
            required: true,
          }),
          dropdownField('resolution-status', 'Resolution Status', ['Unresolved', 'Under Mediation', 'Resolved', 'Escalated Security', 'Escalated Court'], {
            required: true,
          }),
          textField('livelihood-impact', 'Livelihood Impact', {
            required: true,
            layout: FULL_WIDTH,
            placeholder: 'Describe the impact',
            hint: 'Max 500 characters',
          }),
          dropdownField('linked-committee', 'Linked Committee', [], {
            source: 'reference:conflictCommittees',
            placeholder: 'Select committee (optional)',
          }),
          textField('evidence-attachment', 'Evidence Attachment', {
            layout: FULL_WIDTH,
            hint: 'Image or PDF upload — max 10MB, up to 3 files',
          }),
        ],
      },
    ],
  }),
  cloneFormBlueprint({
    id: 'conflict-committee-registration',
    form_name: 'Conflict Committee Registration',
    form_type: 'Standard',
    module_name: 'Conflicts & Dispute Records',
    subtitle: 'Conflict Committee Registration',
    sections: [
      createScopeSection('select-module-conflict-committees', 'conflicts-dispute', 'committees', REGISTRY_SUBMODULE_OPTIONS['conflicts-dispute']),
      {
        id: 'committee-profile',
        title: 'Committee Profile',
        fields: [
          textField('committee-name', 'Committee Name', {
            required: true,
            layout: FULL_WIDTH,
            placeholder: 'e.g. Local Conflict Committee',
            hint: 'Min 5 characters',
          }),
          dropdownField('committee-type', 'Committee Type', ['State CMC', 'LGA CMC', 'Community Peace Committee', 'Traditional Council', 'Interfaith', 'Security Anchored'], {
            required: true,
            placeholder: 'Select committee type',
          }),
          textField('state', 'State', {
            required: true,
            placeholder: 'State',
          }),
          textField('lga', 'LGA', {
            required: true,
            placeholder: 'Local government area',
          }),
          textField('ward-community', 'Ward / Community', {
            required: true,
            placeholder: 'Ward or community',
          }),
          textField('establishment-date', 'Establishment Date', {
            required: true,
            placeholder: 'YYYY-MM-DD',
            hint: 'Must be on or before today',
          }),
          textField('chairperson', 'Chairperson', {
            required: true,
            placeholder: 'Named accountability',
            hint: 'Min 3 characters',
          }),
          numberField('total-members', 'Total Members', {
            required: true,
            placeholder: '0',
            hint: 'Must be >= 1',
          }),
          numberField('trained-members', 'Trained Members', {
            placeholder: '0',
            hint: 'Must be <= Total Members',
          }),
          dropdownField('meeting-frequency', 'Meeting Frequency', ['Weekly', 'Monthly', 'Quarterly', 'As Needed'], {
            required: true,
            placeholder: 'Select meeting frequency',
          }),
          dropdownField('status', 'Status', ['Active', 'Inactive', 'Dissolved'], {
            required: true,
            placeholder: 'Select status',
          }),
        ],
      },
    ],
  }),
  cloneFormBlueprint({
    id: 'transhumance-route-registration',
    form_name: 'Transhumance Route Registration',
    form_type: 'Standard',
    module_name: 'Conflicts & Dispute Records',
    subtitle: 'Transhumance Route Registration',
    sections: [
      createScopeSection('select-module-conflict-routes', 'conflicts-dispute', 'routes', REGISTRY_SUBMODULE_OPTIONS['conflicts-dispute']),
      {
        id: 'route-profile',
        title: 'Route Profile',
        fields: [
          textField('route-name', 'Route Name', {
            required: true,
            layout: FULL_WIDTH,
            placeholder: 'e.g. North Corridor',
            hint: 'Min 5 characters',
          }),
          textField('origin-state', 'Origin State', {
            required: true,
            placeholder: 'Starting state',
          }),
          textField('destination-state', 'Destination State', {
            required: true,
            placeholder: 'Destination state',
          }),
          dropdownField('states-traversed', 'States Traversed', ['Select all that apply'], {
            required: true,
            layout: FULL_WIDTH,
            hint: 'Multi-select — all states along the route',
          }),
          numberField('route-length-km', 'Route Length (km)', {
            required: true,
            placeholder: '0',
            hint: 'Must be >= 0.1',
          }),
          dropdownField('agreement-status', 'Agreement Status', ['Formalised', 'Informal'], {
            required: true,
            placeholder: 'Select agreement status',
          }),
          dropdownField('seasonal-use', 'Seasonal Use', ['Dry Only', 'Wet Only', 'Year Round', 'Dry Primary'], {
            required: true,
            placeholder: 'Select seasonal use',
          }),
          dropdownField('encroachment-status', 'Encroachment Status', ['None', 'Minor', 'Moderate', 'Severe'], {
            required: true,
            placeholder: 'Select encroachment status',
          }),
          dropdownField('primary-species', 'Primary Species', ['Cattle', 'Camel', 'Sheep', 'Goat', 'Mixed'], {
            required: true,
            layout: FULL_WIDTH,
            source: 'reference:species',
            hint: 'Multi-select',
          }),
          textField('gps-waypoints', 'GPS Waypoints', {
            layout: FULL_WIDTH,
            placeholder: 'Optional at MVP, mandatory V1 — GeoJSON LineString',
          }),
        ],
      },
    ],
  }),
].map(normalizeFormBlueprint).sort((left, right) => {
  const moduleOrder = [
    'livestock-population',
    'markets-trade',
    'infrastructure',
    'workforce-institutional',
    'planning-reporting',
    'conflicts-dispute',
    'all-core',
  ];
  const submoduleOrder = {
    'livestock-population': ['population'],
    'markets-trade': ['registry', 'prices', 'border', 'vet'],
    infrastructure: ['core', 'status'],
    'workforce-institutional': ['actors', 'deployment', 'coop'],
    'planning-reporting': ['workplans', 'reports'],
    'conflicts-dispute': ['records', 'committees', 'routes'],
    'all-core': ['registry-proposal'],
    'ppp-arrangements': ['arrangements'],
  };
  const leftModule = moduleOrder.indexOf(left.moduleKey);
  const rightModule = moduleOrder.indexOf(right.moduleKey);
  if (leftModule !== rightModule) return (leftModule === -1 ? 999 : leftModule) - (rightModule === -1 ? 999 : rightModule);
  const leftSubmodule = submoduleOrder[left.moduleKey]?.indexOf(left.submoduleKey) ?? 999;
  const rightSubmodule = submoduleOrder[right.moduleKey]?.indexOf(right.submoduleKey) ?? 999;
  if (leftSubmodule !== rightSubmodule) return leftSubmodule - rightSubmodule;
  return String(left.name ?? '').localeCompare(String(right.name ?? ''), undefined, { numeric: true, sensitivity: 'base' });
});

// REG-PPP (the one cross-cutting registry outside the 16 MVP instruments).
FORM_REGISTRY_BLUEPRINTS.push(...[
  ['PPP Arrangement Registry', 'ppp-arrangements', 'arrangements', [
    textField('ppp-arrangement-id', 'PPP Arrangement ID', { placeholder: 'PPP-[YEAR]-[SEQ]', hint: 'Assigned automatically on save' }),
    dropdownField('partner-org', 'Partner Organisation', [], { required: true, layout: FULL_WIDTH, source: 'reference:cooperatives', placeholder: 'Select partner organisation' }),
    dropdownField('consortium-partner-orgs', 'Consortium Partners', [], { layout: FULL_WIDTH, source: 'reference:cooperatives', placeholder: 'Optional — repeatable for consortia', hint: 'Multi-select' }),
    dropdownField('ppp-type', 'PPP Type', ['Infrastructure', 'Service Delivery', 'Financing / Co-Investment', 'Input Supply', 'Processing / Value Addition', 'Market Development', 'Research / Innovation', 'Capacity Building', 'Other'], { required: true, placeholder: 'Select PPP type' }),
    dropdownField('arrangement-model', 'Arrangement Model', ['BOT', 'BOO', 'Concession', 'Management Contract', 'Joint Venture', 'Off-Take', 'Co-Financing', 'MoU', 'Other'], { required: true, placeholder: 'Select model' }),
    dropdownField('facility', 'Facility', [], { source: 'reference:facilities', placeholder: 'Select facility', hint: 'Shown and mandatory only when PPP Type is Infrastructure' }),
    numberField('investment-value-ngn', 'Investment Value (NGN)', { required: true, placeholder: '0' }),
    numberField('fmld-contribution-ngn', 'FMLD Contribution (NGN)', { placeholder: '0', hint: '≤ total investment value' }),
    numberField('private-contribution-ngn', 'Private Contribution (NGN)', { placeholder: '0', hint: '≤ total investment value' }),
    textField('commencement-date', 'Commencement Date', { required: true, placeholder: 'YYYY-MM-DD' }),
    numberField('duration-months', 'Duration (months)', { required: true, placeholder: '0' }),
    textField('expiry-date', 'Expiry Date', { required: true, placeholder: 'YYYY-MM-DD', hint: 'Must be after the Commencement Date' }),
    geolocationField('geographic-scope', 'Geographic Scope', { required: true }),
    dropdownField('status', 'Status', ['Proposed', 'Under Negotiation', 'Active', 'Suspended', 'Completed', 'Terminated'], { required: true, placeholder: 'Select status' }),
    attachmentField('agreement-document', 'Agreement Document', { layout: FULL_WIDTH, hint: 'MoU / contract — PDF upload, max 10MB' }),
  ]],
].map(([name, moduleKey, submoduleKey, fields]) => normalizeFormBlueprint({
  id: slugify(`${moduleKey}-${submoduleKey}`),
  form_name: name,
  form_type: 'Standard',
  moduleKey,
  submoduleKey,
  module_name: resolveModuleName(moduleKey),
  subtitle: resolveSubmoduleName(moduleKey, submoduleKey),
  sections: [{ id: `${submoduleKey}-fields`, title: name, fields }],
})));

export function getRegistrySubmoduleOptions(moduleKey) {
  if (CATALOG_MODULE_IDS.has(moduleKey)) return getModuleSubmodules(moduleKey);
  return REGISTRY_SUBMODULE_OPTIONS[moduleKey] ?? [];
}

export function resolveFormScopeLabel(form) {
  // Live catalog name wins over whatever was cached on the form at
  // creation/last-save time, so a rename made via the Module Scope panel
  // shows up on every card immediately — the cached name is only a
  // fallback for scope keys the catalog doesn't recognize.
  const moduleLabel = resolveModuleName(form?.moduleKey) || form?.moduleName;
  const submoduleLabel = resolveSubmoduleName(form?.moduleKey, form?.submoduleKey) || form?.submoduleName;
  return [moduleLabel, submoduleLabel].filter(Boolean).join(' - ') || moduleLabel || submoduleLabel || '';
}

function isInScope(form, moduleKey, submoduleKey) {
  if (moduleKey && String(form.moduleKey ?? '').trim() !== String(moduleKey).trim()) return false;
  if (submoduleKey && String(form.submoduleKey ?? '').trim() !== String(submoduleKey).trim()) return false;
  return true;
}

function isCustomType(form) {
  return String(form?.type ?? '').toLowerCase() === 'custom';
}

// The Standard form a module's Add Record page is built from — never a
// Custom form that happens to share the scope.
export function getStandardFormForScope(forms = [], moduleKey, submoduleKey) {
  return getFormBlueprintForScope(forms.filter((form) => !isCustomType(form)), moduleKey, submoduleKey);
}

// Every Custom form bound to this scope (there can be more than one).
export function getCustomFormsForScope(forms = [], moduleKey, submoduleKey) {
  return forms.filter((form) => isCustomType(form) && form.moduleKey && form.submoduleKey && isInScope(form, moduleKey, submoduleKey));
}

// A field that ships with a Standard form's code default. Module pages and
// the backend depend on these, so they can be edited but not removed.
export function isBuiltInStandardField(form, fieldId) {
  if (!form || isCustomType(form)) return false;
  const defaults = FORM_REGISTRY_BLUEPRINTS.find((blueprint) => blueprint.id === form.id);
  return (defaults?.sections ?? []).some((section) => (section.fields ?? []).some((field) => field.id === fieldId));
}

// Fields an admin added to a Standard form in Form Registry — i.e. present
// in the (modified) form but not in its code default. Grouped by section so
// the module page can show them under the section they were added to.
export function getAddedStandardFields(form) {
  if (!form || isCustomType(form)) return [];
  const defaults = FORM_REGISTRY_BLUEPRINTS.find((blueprint) => blueprint.id === form.id);
  const defaultIds = new Set((defaults?.sections ?? []).flatMap((section) => (section.fields ?? []).map((field) => field.id)));
  return (form.sections ?? [])
    .map((section) => ({
      id: section.id,
      title: section.title,
      fields: (section.fields ?? []).filter((field) => !defaultIds.has(field.id)),
    }))
    .filter((section) => section.fields.length > 0);
}

export function getFormBlueprintForScope(forms = [], moduleKey, submoduleKey) {
  const scoped = forms.filter((form) => isInScope(form, moduleKey, submoduleKey));

  if (scoped.length === 0) return null;

  const rank = (form) => {
    const statusRank = String(form.status ?? '').toLowerCase() === 'published' ? 0 : 1;
    const versionRank = Number(form.version ?? 0) * -1;
    const updatedRank = form.updatedAt ? -Date.parse(form.updatedAt) : 0;
    return [statusRank, versionRank, updatedRank];
  };

  return scoped.sort((left, right) => {
    const [leftStatus, leftVersion, leftUpdated] = rank(left);
    const [rightStatus, rightVersion, rightUpdated] = rank(right);
    if (leftStatus !== rightStatus) return leftStatus - rightStatus;
    if (leftVersion !== rightVersion) return leftVersion - rightVersion;
    if (leftUpdated !== rightUpdated) return leftUpdated - rightUpdated;
    return String(left.name ?? left.form_name ?? '').localeCompare(String(right.name ?? right.form_name ?? ''), undefined, { numeric: true, sensitivity: 'base' });
  })[0];
}
